import axios from 'axios';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from '@redux-devtools/extension';
import thunkMiddleWare from 'redux-thunk';
import { triggerScroll } from 'shared/helpers';
import { ADD_LOANPART, BROKEN, BUSY, DELETE_LOANPART, INIT, INPUT, LOANPART, NAVIGATE, RESET, SYNC, UPDATE } from '../../shared/wizard/actions';
import initState, { loanpart } from './config';

const framed = location.pathname.split('/')[2] === 'framed';
initState.framed = framed;

let storage;

axios.interceptors.response.use(
  null,
  error =>
    new Promise((_resolve, reject) => {
      if (error.response.status !== 422) storage.dispatch({ type: BROKEN });
      return reject(error);
    })
);

const validate = (page, input, callback) => {
  axios
    .post(`/kidwk/calculate/${page}`, input)
    .then(({ data: payload }) => {
      if (payload?.length === 0) payload = {};
      storage.dispatch({ type: SYNC, payload });
      callback();
    })
    .catch(({ response = {} }) => {
      const { errors } = response.data || {};
      storage.dispatch({ type: SYNC, syncing: false, payload: errors ? { errors } : {} });
    });
};

const navigate = (state, action) => {
  if (state.syncing) return state;
  const n = action.delta || 0; // n == 1 returns the next page route
  const { page = state.pages[0] } = action.location.state
  let index = typeof action.index === 'number' ? action.index : state.pages.indexOf(page);

  if (index === -1) index = 0;
  index += n;

  const newPage = state.pages[index] || state.pages[0];
  const update = `/${state.app}${state.framed ? '/framed' : ''}/${newPage + action.location.search}`;
  const requireValidation = n > -1 && newPage !== state.pages[0] && page !== state.pages.slice(-1)[0];

  const done = () => {
    action.navigate(update, {state: { page: newPage }});
    triggerScroll();
  };

  if (requireValidation) {
    validate(page, state.input, () => {
      if (!state.pages[index - 1] || page === state.pages[index - 1]) return done();
      return validate(state.pages[index - 1], state.input, done);
    });
  } else done();

  return {
    ...state,
    syncing: state.syncing || requireValidation,
    recalculating: action.recalculate ?? state.recalculating,
  };
};

const reset = (state, action) => {
  action.navigate(`/${state.app}${state.framed ? '/framed' : ''}`, { state: { page: state.pages[0] + action.location.search}});

  return {
    ...initState,
    syncing: false,
  };
};

const busy = state => ({
  ...state,
  syncing: true,
});

const input = (state, action) => ({
  ...state,
  input: {
    ...state.input,
    ...action.payload,
  },
});

const sync = (state, action) => {
  const payload = action.payload || {};
  if (payload.possibility === null) payload.possibility = {};
  return {
    ...state,
    syncing: typeof action.syncing === 'boolean' ? action.syncing : state.syncing,
    recalculating: action.recalculate ?? state.recalculating,
    output: {
      ...state.output,
      errors: {},
      ...payload,
    },
  };
};

const init = (state, { payload }) => {
  const pages = [...state.pages];
  if (payload?.settings?.askCurrentMortgage && !pages.includes('current-mortgage')) {
    pages.splice(3, 0, 'current-mortgage');
  }
  if (payload?.settings?.showLeadInformationSwitch && !pages.includes('applicant-information')) {
    pages.splice(4, 0, 'applicant-information');
  }
  return { ...state, ...payload, pages };
};

const inputLoanpart = (state, { payload, index }) => {
  const loanparts = [...state.input.loanparts];
  loanparts[index] = { ...loanparts[index], ...payload };
  return {
    ...state,
    input: {
      ...state.input,
      loanparts,
    },
  };
};

const addLoanpart = state => ({
  ...state,
  input: {
    ...state.input,
    loanparts: [...state.input.loanparts, loanpart],
  },
});

const deleteLoanpart = (state, { payload }) => {
  return {
    ...state,
    input: {
      ...state.input,
      loanparts: state.input.loanparts.filter((_, i) => i !== payload),
    },
  };
};

function reducer(state, action) {
  switch (action.type) {
    case INIT:
      return init(state, action);
    case RESET:
      return reset(state, action);
    case INPUT:
      return input(state, action);
    case BUSY:
      return busy(state);
    case SYNC:
      return sync(state, action);
    case NAVIGATE:
      return navigate(state, action);
    case BROKEN:
      return { ...state, broken: true };
    case UPDATE:
      return { ...state, ...action.payload };
    case LOANPART:
      return inputLoanpart(state, action);
    case ADD_LOANPART:
      return addLoanpart(state, action);
    case DELETE_LOANPART:
      return deleteLoanpart(state, action);
    default:
      return state;
  }
}

export const enhancer = composeWithDevTools(applyMiddleware(thunkMiddleWare));

export const store = createStore(reducer, initState, enhancer);

storage = store;
